// src/components/Admin/AdminPayOuts.js
import React, { useState, useMemo, useEffect } from "react";
import "../../styles/AdminPayOuts.css";
import config from "../../config";

export default function AdminPayOuts({ sellerId = null, sellerName = "" }) {
  // ----- Date defaults: current month -----
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(todayStr);

  const [payouts, setPayouts] = useState([]);
  const [paymentRefs, setPaymentRefs] = useState({});
  const [loadingLoad, setLoadingLoad] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | GENERATED | PAID

  // 🔐 admin token
  const token = localStorage.getItem("admin_id_token");

  const formatCurrency = (num) => {
    if (num == null || isNaN(num)) return "-";
    return `₹${parseFloat(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    return `${formatDate(dateString)} ${d.toTimeString().slice(0, 5)}`; // HH:mm
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  const buildHeaders = () => {
    const headers = { Accept: "application/json" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  // Mark a payout as "overdue" if Generated and periodEnd is >7 days ago
  const isOverdue = (payout) => {
    if (!payout || payout.status !== "GENERATED") return false;
    const end = new Date(payout.periodEnd);
    const now = new Date();
    const diffDays = (now - end) / (1000 * 60 * 60 * 24);
    return diffDays > 7;
  };

  // ----------- LOAD PAYOUTS (history) -----------
  const handleLoad = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From and To dates.");
      return;
    }

    setLoadingLoad(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("from", fromDate);
      params.append("to", toDate);
      if (sellerId) {
        params.append("sellerId", sellerId);
      }

      const url = `${config.baseUrl}/api/admin/payouts?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: buildHeaders(),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load payouts");
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setPayouts(list);

      // pre-fill paymentRefs from paymentReference
      const refMap = {};
      list.forEach((p) => {
        if (p.paymentReference) {
          refMap[p.id] = p.paymentReference;
        }
      });
      setPaymentRefs(refMap);
    } catch (err) {
      console.error("Load payouts error:", err);
      setError(err.message || "Unable to load payouts");
    } finally {
      setLoadingLoad(false);
    }
  };

  // Auto-load for current month on first render
  useEffect(() => {
    handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------- GENERATE PAYOUTS -----------
  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From and To dates.");
      return;
    }

    setLoadingGenerate(true);
    setError("");
    setSuccess("");

    try {
      const url = `${config.baseUrl}/api/admin/payouts/generate?from=${fromDate}&to=${toDate}`;
      const res = await fetch(url, {
        method: "POST",
        headers: buildHeaders(),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to generate payouts");
      }

      const data = await res.json();
      const generatedCount = Array.isArray(data) ? data.length : 0;

      // After generating, reload the full list for that period
      await handleLoad();

      if (generatedCount === 0) {
        showSuccess("No new payouts to generate for this period.");
      } else {
        showSuccess(
          `Generated ${generatedCount} payout${
            generatedCount > 1 ? "s" : ""
          } for the selected period.`
        );
      }
    } catch (err) {
      console.error("Generate payouts error:", err);
      setError(err.message || "Unable to generate payouts");
    } finally {
      setLoadingGenerate(false);
    }
  };

  // ----------- MARK AS PAID -----------
  const handlePaymentRefChange = (id, value) => {
    setPaymentRefs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleMarkPaid = async (payout) => {
    const ref = paymentRefs[payout.id];

    if (!ref || !ref.trim()) {
      alert("Please enter a payment reference (UTR / bank ref).");
      return;
    }

    if (
      !window.confirm(
        `Mark payout #${payout.id} for "${payout.sellerName}" as PAID?`
      )
    ) {
      return;
    }

    setUpdatingId(payout.id);
    setError("");
    setSuccess("");

    try {
      const url = `${config.baseUrl}/api/admin/payouts/${
        payout.id
      }/mark-paid?paymentReference=${encodeURIComponent(ref.trim())}`;

      const res = await fetch(url, {
        method: "POST",
        headers: buildHeaders(),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to mark payout as paid");
      }

      const updated = await res.json();

      setPayouts((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p))
      );

      showSuccess(
        `Payout #${updated.id} marked as PAID (₹${updated.sellerEarning}).`
      );
    } catch (err) {
      console.error("Mark paid error:", err);
      setError(err.message || "Unable to mark payout as paid");
    } finally {
      setUpdatingId(null);
    }
  };

  // ----------- FILTER + SUMMARY IN UI -----------

  const visiblePayouts = useMemo(() => {
    let list = payouts;
    if (sellerId) {
      list = list.filter((p) => p.sellerId === sellerId);
    }
    if (statusFilter !== "ALL") {
      list = list.filter((p) => p.status === statusFilter);
    }
    return list;
  }, [payouts, sellerId, statusFilter]);

  const summary = useMemo(() => {
    if (!visiblePayouts.length) {
      return {
        totalGross: 0,
        totalCommission: 0,
        totalEarning: 0,
        paidCount: 0,
        generatedCount: 0,
      };
    }

    return visiblePayouts.reduce(
      (acc, p) => {
        acc.totalGross += Number(p.grossAmount || 0);
        acc.totalCommission += Number(p.commissionAmount || 0);
        acc.totalEarning += Number(p.sellerEarning || 0);
        if (p.status === "PAID") acc.paidCount += 1;
        if (p.status === "GENERATED") acc.generatedCount += 1;
        return acc;
      },
      {
        totalGross: 0,
        totalCommission: 0,
        totalEarning: 0,
        paidCount: 0,
        generatedCount: 0,
      }
    );
  }, [visiblePayouts]);

  // ----------- RENDER -----------

  return (
    <div className="payouts-container">
      <div className="payouts-header">
        <div>
          <h2>💰 Seller Payouts</h2>
          <p>Generate and mark payouts as paid for each seller.</p>
          <p className="payouts-subtext">
            <strong>Load Payouts</strong> = see existing payouts for the
            selected period.{" "}
            <strong>Generate Payouts</strong> = create new payouts for PAID
            orders that are not yet included.
          </p>

          {sellerId && (
            <p style={{ marginTop: 4, fontSize: 13, color: "#0f172a" }}>
              Showing payouts for <strong>Seller #{sellerId}</strong>
              {sellerName ? ` – ${sellerName}` : ""}.
            </p>
          )}
        </div>

        <div className="payouts-filters">
          <div className="payouts-date-field">
            <label>From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="payouts-date-field">
            <label>To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div className="payouts-status-filter">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="GENERATED">Generated</option>
              <option value="PAID">Paid</option>
            </select>
          </div>

          <button
            className="payouts-load-btn"
            onClick={handleLoad}
            disabled={loadingLoad}
          >
            {loadingLoad ? "Loading..." : "Load Payouts"}
          </button>

          <button
            className="payouts-generate-btn"
            onClick={handleGenerate}
            disabled={loadingGenerate}
          >
            {loadingGenerate ? "Generating..." : "Generate Payouts"}
          </button>
        </div>
      </div>

      {success && <p className="success">✅ {success}</p>}
      {error && <p className="error">⚠ {error}</p>}

      {visiblePayouts.length > 0 && (
        <div className="payouts-summary-cards">
          <div className="summary-card">
            <span className="label">Total Gross</span>
            <span className="value">
              {formatCurrency(summary.totalGross)}
            </span>
          </div>
          <div className="summary-card">
            <span className="label">Total Commission</span>
            <span className="value">
              {formatCurrency(summary.totalCommission)}
            </span>
          </div>
          <div className="summary-card">
            <span className="label">Total Seller Earnings</span>
            <span className="value">
              {formatCurrency(summary.totalEarning)}
            </span>
          </div>
          <div className="summary-card small">
            <span className="label">Generated</span>
            <span className="value">{summary.generatedCount}</span>
          </div>
          <div className="summary-card small">
            <span className="label">Paid</span>
            <span className="value">{summary.paidCount}</span>
          </div>
        </div>
      )}

      <div className="payouts-table-wrap">
        <table className="payouts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Seller</th>
              <th>Period</th>
              <th>Gross</th>
              <th>Commission</th>
              <th>Seller Earning</th>
              <th>Status</th>
              <th>Payment Ref</th>
              <th>Paid At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visiblePayouts.length === 0 ? (
              <tr>
                <td colSpan={10} className="payouts-empty">
                  No payouts for the selected period.
                  <br />
                  Choose dates and click "Load Payouts" or "Generate Payouts".
                </td>
              </tr>
            ) : (
              visiblePayouts.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>
                    <div className="payouts-seller">
                      <span className="payouts-seller-name">
                        {p.sellerName}
                      </span>
                      <span className="payouts-seller-id">
                        ID: {p.sellerId}
                      </span>
                    </div>
                  </td>
                  <td>
                    {formatDate(p.periodStart)} – {formatDate(p.periodEnd)}
                  </td>
                  <td>{formatCurrency(p.grossAmount)}</td>
                  <td>{formatCurrency(p.commissionAmount)}</td>
                  <td>{formatCurrency(p.sellerEarning)}</td>
                  <td>
                    <span
                      className={`payouts-status ${
                        p.status === "PAID" ? "paid" : "generated"
                      }`}
                    >
                      {p.status}
                    </span>
                    {isOverdue(p) && (
                      <span className="payouts-overdue-badge">Overdue</span>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="payouts-ref-input"
                      placeholder="UTR / Bank Ref"
                      value={paymentRefs[p.id] ?? (p.paymentReference || "")}
                      onChange={(e) =>
                        handlePaymentRefChange(p.id, e.target.value)
                      }
                      disabled={p.status === "PAID"}
                    />
                  </td>
                  <td>{formatDateTime(p.paidAt)}</td>
                  <td>
                    <button
                      className="payouts-pay-btn"
                      disabled={p.status === "PAID" || updatingId === p.id}
                      onClick={() => handleMarkPaid(p)}
                    >
                      {p.status === "PAID"
                        ? "Paid"
                        : updatingId === p.id
                        ? "Saving..."
                        : "Mark Paid"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
